import React, { useRef } from 'react'
import { isCarouselWidgetType } from '../../constants/widgetTypes'
import { WidgetFormState } from '../../hooks'
import formStyles from '../../styles/forms.module.scss'
import { Badge } from '../Badge'
import WidgetMetadataEditor from './metadata-editors'
import WidgetTypeSelector from './WidgetTypeSelector'

interface WidgetFormProps {
  state: WidgetFormState
  namePlaceholder?: string
}

const WidgetForm: React.FC<WidgetFormProps> = ({ state, namePlaceholder }) => {
  const { name, setName, type, setType, metadata, setMetadata, widgetId, autoCreating, typeLocked, handleNameBlur } = state

  const isCarousel = isCarouselWidgetType(type)
  const title = typeof metadata.title === 'string' ? metadata.title : ''

  // Mirror Name → Title only until the user takes ownership of Title (edits it,
  // or the widget loaded with a Title already set). Parents reuse one WidgetForm
  // instance across widgets via formState.reset() rather than remounting it, so
  // re-evaluate the link whenever a different widget loads in (widgetId change).
  const titleLinked = useRef(title === '')
  const syncedWidgetId = useRef(widgetId)
  if (syncedWidgetId.current !== widgetId) {
    syncedWidgetId.current = widgetId
    titleLinked.current = title === ''
  }

  const handleNameChange = (v: string) => {
    setName(v)
    if (isCarousel && titleLinked.current) setMetadata({ ...metadata, title: v })
  }

  const handleTitleChange = (v: string) => {
    titleLinked.current = false
    setMetadata({ ...metadata, title: v })
    // Prefill Name from Title while Name is still empty.
    if (name === '') setName(v)
  }

  const nameField = (
    <div className={formStyles.fieldGroup}>
      <label>Name <Badge variant="neutral" size="small">Internal</Badge></label>
      <input
        type="text"
        value={name}
        onChange={(e) => handleNameChange(e.target.value)}
        onBlur={() => {
          void handleNameBlur()
        }}
        placeholder={namePlaceholder ?? 'e.g., US Open Hero Banner'}
        disabled={autoCreating}
      />
    </div>
  )

  return (
    <div className={formStyles.formField}>
      {!typeLocked && (
        <div className={formStyles.fieldGroup}>
          <label>Widget Type</label>
          <WidgetTypeSelector
            value={type}
            onChange={(code) => {
              // Re-picking the "Carousel" card while already on a carousel would
              // reset the size to the default and wipe the shared config — skip it.
              if (isCarouselWidgetType(code) && isCarouselWidgetType(type)) return
              setType(code)
              setMetadata({})
            }}
          />
        </div>
      )}

      {type && (
        <>
          {isCarousel ? (
            <div className={formStyles.formGrid}>
              {nameField}
              <div className={formStyles.fieldGroup}>
                <label>Title <Badge variant="secondary" size="small">Public</Badge></label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g., Live & Upcoming"
                />
              </div>
            </div>
          ) : (
            nameField
          )}

          <WidgetMetadataEditor
            widgetType={type}
            metadata={metadata as Record<string, unknown>}
            onChange={(m) => setMetadata(m)}
            onWidgetTypeChange={setType}
            widgetId={widgetId ?? undefined}
          />
        </>
      )}
    </div>
  )
}

export default WidgetForm
